import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { landingPathForRole } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login, getApiErrorMessage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // An explicit "from" (set when a guard bounced the user) wins; otherwise land
  // on the role's default page.
  const from = location.state?.from?.pathname;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user !== false) navigate(from || landingPathForRole(user.role), { replace: true });
  }, [user, from, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      navigate(from || landingPathForRole(u.role), { replace: true });
    } catch (err) {
      const msg = getApiErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: hero panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 10%, hsl(14 56% 58% / 0.45), transparent 40%),radial-gradient(circle at 80% 80%, hsl(0 0% 100% / 0.15), transparent 40%)",
          }}
        />
        <div className="relative z-10 flex items-center gap-2">
          <div className="h-10 w-10 rounded-md bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center">
            <ShieldCheck size={22} weight="duotone" />
          </div>
          <div>
            <div className="font-display text-xl">Broker OS</div>
            <div className="text-[10px] tracking-[0.24em] uppercase opacity-70">Insurance Broker Mgmt</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h2 className="font-display text-4xl leading-[1.1] tracking-tight">
            The ledger of trust for every policy you write.
          </h2>
          <p className="text-sm opacity-80 mt-4 leading-relaxed">
            Manage insurers, agents, premiums & commissions across LIC, HDFC, ICICI, Star Health and more —
            with GST-ready invoicing and a configurable payout grid.
          </p>
        </div>
        <div className="relative z-10 text-xs opacity-70">
          Secured by JWT + role-based access • Admin · Agent · Accountant
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <ShieldCheck size={20} weight="duotone" />
            </div>
            <div className="font-display text-lg">Broker OS</div>
          </div>

          <div className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground font-semibold">
            Sign in
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Welcome back.</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Use your broker credentials to access the platform.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4" data-testid="login-form">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div
                className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} className="mt-2 gap-2" data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} />
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-8 text-center">
            New broker?{" "}
            <a href="/signup" className="text-primary underline" data-testid="goto-signup">Create your workspace</a>
          </p>
        </div>
      </div>
    </div>
  );
}
